import { accentSelectedText, type TileFamilyAccent } from "@ui/lib/accent-classes";
import { tv, type VariantProps } from "@ui/lib/tv";

type ToggleTileAccent = Exclude<TileFamilyAccent, "red">;

/**
 * Per-accent ring/outline literals for ToggleTile. The accent shades are
 * chosen to match the illustrated icons, so green/purple deliberately sit one
 * step lighter than Tile's ring shades (green-400 vs green-500, purple-500 vs
 * purple-600); neutral hovers lighter (300) than it selects (400). The
 * selected-caption map is shared via `@ui/lib/accent-classes`.
 * Full literal strings so the Tailwind v4 scanner emits them.
 */
const TOGGLE_TILE_ACCENTS: Record<ToggleTileAccent, { hoverRing: string; selectedRing: string; focusOutline: string }> =
  {
    green: {
      hoverRing: "shadow-[inset_0_0_0_1px_var(--color-green-400)]",
      selectedRing: "shadow-[inset_0_0_0_2px_var(--color-green-400)]",
      focusOutline: "group-focus-visible/tt:outline-green-400",
    },
    yellow: {
      hoverRing: "shadow-[inset_0_0_0_1px_var(--color-yellow-500)]",
      selectedRing: "shadow-[inset_0_0_0_2px_var(--color-yellow-500)]",
      focusOutline: "group-focus-visible/tt:outline-yellow-500",
    },
    purple: {
      hoverRing: "shadow-[inset_0_0_0_1px_var(--color-purple-500)]",
      selectedRing: "shadow-[inset_0_0_0_2px_var(--color-purple-500)]",
      focusOutline: "group-focus-visible/tt:outline-purple-500",
    },
    pink: {
      hoverRing: "shadow-[inset_0_0_0_1px_var(--color-pink-400)]",
      selectedRing: "shadow-[inset_0_0_0_2px_var(--color-pink-400)]",
      focusOutline: "group-focus-visible/tt:outline-pink-400",
    },
    neutral: {
      hoverRing: "shadow-[inset_0_0_0_1px_var(--color-neutral-300)]",
      selectedRing: "shadow-[inset_0_0_0_2px_var(--color-neutral-400)]",
      focusOutline: "group-focus-visible/tt:outline-neutral-400",
    },
  };

const TOGGLE_TILE_COLORS = Object.keys(TOGGLE_TILE_ACCENTS) as ToggleTileAccent[];

/**
 * The hover/selected surface tint: every accent's -50 rung, which is what
 * `bg-primary` resolves to inside `accentScope(color)`. Figma's Neutral
 * collection has no such rung (`bg-primary` is white there), so neutral keeps
 * the literal.
 */
const surfaceTint = (color: ToggleTileAccent) => (color === "neutral" ? "bg-neutral-50" : "bg-primary");

const accentCompoundVariants = TOGGLE_TILE_COLORS.flatMap((color) => {
  const accent = TOGGLE_TILE_ACCENTS[color];
  return [
    // Hover (not selected): light tint + single-weight colored ring
    { color, isHovered: true, isSelected: false, class: { surface: `${surfaceTint(color)} ${accent.hoverRing}` } },
    // Selected: tinted surface + 2px colored ring (both orientations)
    { color, isSelected: true, class: { surface: `${surfaceTint(color)} ${accent.selectedRing}` } },
    // Caption colour: only when the label sits INSIDE the tinted surface
    // (horizontal pills). Vertical captions live outside the tile, so they
    // bold but keep the resting text colour.
    {
      color,
      isSelected: true,
      orientation: "horizontal" as const,
      class: { caption: accentSelectedText[color] },
    },
  ];
});

/**
 * A selectable icon + label toggle with two presentations that share one styling
 * system:
 *
 * - `vertical` — a square icon surface with the caption below it (filter cards).
 * - `horizontal` — a pill with the icon and label inline (filter chips).
 *
 * The `color` variant is the accent for hover/selected states and is chosen to
 * match the icon, so a green backpack pairs with `color="green"`, a pink heart
 * with `color="pink"`, etc. Selected/hover styling lives on the `surface` slot
 * (the icon box for vertical, the whole pill for horizontal); the focus ring is
 * driven from the root so it wraps the surface in both presentations.
 */
export const toggleTile = tv({
  slots: {
    root: "group/tt relative flex cursor-pointer outline-none disabled:cursor-not-allowed disabled:opacity-50",
    surface: [
      "relative flex items-center justify-center bg-white transition-[background-color,box-shadow]",
      "group-focus-visible/tt:outline-2 group-focus-visible/tt:outline-offset-2",
    ],
    icon: "inline-flex shrink-0",
    caption: "text-neutral-900 leading-6 transition-colors",
  },
  defaultVariants: {
    orientation: "horizontal",
    color: "neutral",
  },
  variants: {
    orientation: {
      vertical: {
        root: "w-full flex-col items-center gap-3 text-center",
        surface: "aspect-square w-full rounded-3xl [&_svg]:size-10",
        caption: "text-base",
      },
      horizontal: {
        root: "w-full",
        surface: "h-[52px] w-full items-center justify-start gap-2 rounded-2xl px-3 py-2 [&_svg]:size-8",
        caption: "flex-1 text-left text-base",
      },
    },
    color: {
      green: { surface: TOGGLE_TILE_ACCENTS.green.focusOutline },
      yellow: { surface: TOGGLE_TILE_ACCENTS.yellow.focusOutline },
      purple: { surface: TOGGLE_TILE_ACCENTS.purple.focusOutline },
      pink: { surface: TOGGLE_TILE_ACCENTS.pink.focusOutline },
      neutral: { surface: TOGGLE_TILE_ACCENTS.neutral.focusOutline },
    },
    isHovered: { true: {} },
    isSelected: { true: {} },
  },
  compoundVariants: [
    // Default + hover-without-selection share the resting hairline border unless a
    // colored hover ring replaces it below.
    {
      isSelected: false,
      isHovered: false,
      class: {
        surface: "shadow-keyline-inset",
      },
    },

    ...accentCompoundVariants,

    // Caption weight: bold when selected in both presentations.
    { isSelected: true, class: { caption: "font-semibold" } },
  ],
});

export type ToggleTileVariants = VariantProps<typeof toggleTile>;
export type ToggleTileOrientation = NonNullable<ToggleTileVariants["orientation"]>;
export type ToggleTileColor = NonNullable<ToggleTileVariants["color"]>;
